export const normalizeCode = (value: string) => value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6);

const normalizeQrToken = (value: string) => {
  const token = value.trim();
  if (/^[A-Za-z0-9]{6}$/.test(token)) return token.toUpperCase();
  return token.length >= 6 && token.length <= 255 ? token : null;
};

export function codeFromQr(raw: string): string | null {
  const direct = normalizeCode(raw);
  if (direct.length === 6 && raw.trim().length <= 8) return direct;
  try {
    const parsed = JSON.parse(raw) as { code?: string; codigo?: string };
    return normalizeQrToken(parsed.code ?? parsed.codigo ?? '');
  } catch { /* payload may be a URL */ }
  try {
    const url = new URL(raw);
    return normalizeQrToken(url.searchParams.get('code') ?? url.searchParams.get('codigo') ?? '');
  } catch { return null; }
}

export function resolvePresetCode(): string {
  const params = new URL(window.location.href).searchParams;
  const value = (params.get('code') ?? params.get('codigo') ?? '').trim();
  return value && value.length <= 255 && /^[A-Za-z0-9_-]+$/.test(value) ? value : '';
}

export function resolvePresetNas(): string {
  const value = new URL(window.location.href).searchParams.get('nas') ?? '';
  return /^[A-Za-z0-9][A-Za-z0-9._:-]{0,119}$/.test(value) ? value : '';
}

export function resolveCaptiveContext(): string {
  const value = new URL(window.location.href).searchParams.get('context') ?? '';
  return /^v1\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]{43}$/.test(value) && value.length <= 2_048 ? value : '';
}

export function resolveCaptiveRenewUrl(): string {
  const value = new URL(window.location.href).searchParams.get('renew') ?? '';
  if (!value || value.length > 512) return '';
  try {
    const url = new URL(value, window.location.origin);
    if (url.origin !== window.location.origin || url.pathname !== '/captive/renew') return '';
    const ticket = url.searchParams.get('ticket') ?? '';
    if (!/^[A-Za-z0-9_-]{43}$/.test(ticket) || Array.from(url.searchParams.keys()).some((key) => key !== 'ticket')) return '';
    return `${url.pathname}?${url.searchParams.toString()}`;
  } catch {
    return '';
  }
}

export function replaceCaptiveContext(context: string, renewal?: string): void {
  if (!/^v1\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]{43}$/.test(context) || context.length > 2_048) {
    throw new Error('El punto WiFi devolvió una autorización no válida.');
  }
  const url = new URL(window.location.href);
  url.searchParams.set('context', context);
  if (renewal) {
    const candidate = new URL(renewal, window.location.origin);
    const ticket = candidate.searchParams.get('ticket') ?? '';
    if (candidate.origin !== window.location.origin || candidate.pathname !== '/captive/renew' || !/^[A-Za-z0-9_-]{43}$/.test(ticket) || Array.from(candidate.searchParams.keys()).some((key) => key !== 'ticket')) {
      throw new Error('El punto WiFi devolvió una renovación no válida.');
    }
    url.searchParams.set('renew', `${candidate.pathname}?${candidate.searchParams.toString()}`);
  }
  window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
}

export function externalBrowserHref(): string {
  const current = new URL(window.location.href);
  current.searchParams.set('external', '1');
  if (/Android/i.test(navigator.userAgent)) {
    return `intent://${current.host}${current.pathname}${current.search}#Intent;scheme=${current.protocol.slice(0, -1)};package=com.android.chrome;end`;
  }
  return current.href;
}

export function isExternalBrowserHandoff(userAgent = navigator.userAgent): boolean {
  if (new URL(window.location.href).searchParams.get('external') === '1') return true;
  const androidChrome = /Android/i.test(userAgent)
    && /Chrome\//i.test(userAgent)
    && !/;\s*wv\)/i.test(userAgent)
    && !/Version\/4\.0/i.test(userAgent);
  const fullSafari = !/Android/i.test(userAgent)
    && /Safari\//i.test(userAgent)
    && !/(CriOS|FxiOS|CaptiveNetworkSupport)/i.test(userAgent);
  return androidChrome || fullSafari;
}
